import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgRole } from '@nargus/shared';
import { CircleAlert, LoaderCircle, Trash2 } from 'lucide-react';
import { useErrorText } from '@/lib/error-text';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function useRoleLabels(): Record<OrgRole, string> {
  const { t } = useTranslation();
  return {
    owner: t('roles.owner'),
    member: t('roles.member'),
    friend: t('roles.friend'),
  };
}

const DATE_FORMAT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

export function formatDate(iso: string): string {
  return DATE_FORMAT.format(new Date(iso));
}

const RELATIVE_FORMAT = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });
const DAY_MS = 86_400_000;

export function daysUntil(iso: string): number {
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / DAY_MS);
}

export function formatRelativeDays(days: number): string {
  return RELATIVE_FORMAT.format(days, 'day');
}

const EURO_FORMAT = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

export function formatEuro(cents: number): string {
  return EURO_FORMAT.format(cents / 100);
}

export function toDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function fromDateInput(value: string): string | null {
  return value ? new Date(`${value}T08:00:00`).toISOString() : null;
}

export function toAmountInput(cents: number | undefined): string {
  return cents === undefined ? '' : String(cents / 100);
}

export function toCents(value: string): number {
  return Math.round(Number(value) * 100);
}

const ROLE_VARIANT: Record<OrgRole, 'default' | 'secondary' | 'outline'> = {
  owner: 'default',
  member: 'secondary',
  friend: 'outline',
};

export function RoleBadge({ role }: { role: OrgRole }) {
  const labels = useRoleLabels();
  return <Badge variant={ROLE_VARIANT[role]}>{labels[role]}</Badge>;
}

export function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div>
      <h1 className="font-heading text-xl font-medium">{title}</h1>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const errorText = useErrorText();
  if (!error) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm text-destructive">
      <CircleAlert className="size-4 shrink-0" />
      {errorText(error)}
    </p>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  busy?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <ErrorNote error={error} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? <LoaderCircle className="size-4 animate-spin" /> : <Trash2 />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


